// What an object is called (docs/claude/names.md): the name the user ENTERED, as
// typed, in every language; else its DEFAULT name in the language asked. The one
// place that decides it: no display site picks between an entered name and
// translations on its own. A display number ("Sauna 01") is added by the caller
// (renderer/naming.ts `numberedNames`), to an entered name as to a default, and
// never stored. Pure and DOM-free (test/names.test.mjs).

import type {
  Lang,
  ProductInstance,
  ProductTemplate,
  TextI18n,
} from './types';

/** The default languages: each nameable object has a default in all three. */
export const NAME_LANGS: readonly Lang[] = ['en', 'it', 'fr'];

/** An object's default name in `lang`: that language's, else English, else
 *  `base` (a product's own `name`: English for a built-in, its first language
 *  for one saved before every product carried all three), else Italian, else
 *  French — the first not empty (names.md §2, no warning). */
export function defaultName(defaults: TextI18n | undefined, lang: Lang, base = ''): string {
  for (const v of [defaults?.[lang], defaults?.en, base, defaults?.it, defaults?.fr]) {
    const t = v?.trim();
    if (t) return t;
  }
  return '';
}

/** What an object is called in `lang`: the name entered for it when not empty,
 *  the same in every language; else its default. */
export function shownName(entered: string | undefined, defaults: TextI18n | undefined, lang: Lang, base = ''): string {
  return entered?.trim() || defaultName(defaults, lang, base);
}

/** A catalogue product's name in `lang`, or a part placed from one: the name
 *  entered for the part, else its catalogue names (a placed copy keeps a copy
 *  of them). */
export function productName(p: Pick<ProductTemplate | ProductInstance, 'name' | 'nameI18n'> & { enteredName?: string }, lang: Lang): string {
  return shownName(p.enteredName, p.nameI18n, lang, p.name);
}

/** The defaults of something the app names itself: its dictionary key in each
 *  language (`trIn`, renderer/i18n.ts), with `suffix` after it ("Sheet" + " 3"). */
export function dictDefaults(key: string, trIn: (key: string, lang: Lang) => string, suffix = ''): TextI18n {
  const out: TextI18n = {};
  for (const l of NAME_LANGS) out[l] = `${trIn(key, l)}${suffix}`;
  return out;
}

/** The names a catalogue product is stored by, `name` as the English where none
 *  is stated (names.md §4): what a rename or a duplicate starts from. */
function storedNames(p: Pick<ProductTemplate, 'name' | 'nameI18n'>): TextI18n {
  const out: TextI18n = {};
  for (const l of NAME_LANGS) {
    const v = (l === 'en' ? p.nameI18n?.en || p.name : p.nameI18n?.[l])?.trim();
    if (v) out[l] = v;
  }
  return out;
}

/** The names a catalogue product lacks: per name, the languages it has no
 *  default in ("IT, FR", a part's "FR (Fan)"), none when it is named in all
 *  three (names.md §4) — what a save to the catalogue asks for. */
export function missingNames(p: Pick<ProductTemplate, 'name' | 'nameI18n' | 'parts'>): string[] {
  const out: string[] = [];
  const check = (x: Pick<ProductTemplate, 'name' | 'nameI18n'>, where: string): void => {
    const has = storedNames(x);
    const langs = NAME_LANGS.filter((l) => !has[l]).map((l) => l.toUpperCase()).join(', ');
    if (langs) out.push(where ? `${langs} (${where})` : langs);
  };
  check(p, '');
  for (const [i, q] of (p.parts ?? []).entries()) check(q, String(q.name ?? '').trim() || `#${i + 1}`);
  return out;
}

/** Names stored as `name` (the English, else the first) and `nameI18n`. */
function asStored(names: TextI18n): Pick<ProductTemplate, 'name' | 'nameI18n'> {
  return { name: names.en ?? names.it ?? names.fr ?? '', nameI18n: names };
}

/** A catalogue product renamed in `lang` (its card's Rename, in the UI's
 *  language): that language's name, the others kept. */
export function renamedNames(p: Pick<ProductTemplate, 'name' | 'nameI18n'>, lang: Lang, text: string): Pick<ProductTemplate, 'name' | 'nameI18n'> {
  return asStored({ ...storedNames(p), [lang]: text.trim() });
}

/** A duplicate's names: "… copy" in each language the product is named in
 *  (`trIn` of '{name} copy'); a language it lacks stays lacking. */
export function copiedNames(p: Pick<ProductTemplate, 'name' | 'nameI18n'>, trIn: (key: string, lang: Lang) => string): Pick<ProductTemplate, 'name' | 'nameI18n'> {
  const out: TextI18n = {};
  for (const [l, v] of Object.entries(storedNames(p)) as Array<[Lang, string]>) out[l] = trIn('{name} copy', l).replace('{name}', v);
  return asStored(out);
}

/** A name the user typed, made a catalogue product's (Save as product, a
 *  heater's brand and model): as typed in every language, as the drawing
 *  showed it. */
export function typedNames(text: string): Pick<ProductTemplate, 'name' | 'nameI18n'> {
  const t = text.trim(), out: TextI18n = {};
  for (const l of NAME_LANGS) out[l] = t;
  return asStored(out);
}

/** A catalogue row's names filled from the code's for the same product (the
 *  names report's proposal, names.md §5): a language it lacks, or whose slot
 *  only repeats its English where the code has a word of its own, takes the
 *  code's; a name of its own is kept, and so is `name`. A part only from the
 *  same part: its id and its English. Null when nothing changes. */
export function namesFromCode(stored: ProductTemplate, code: ProductTemplate): ProductTemplate | null {
  let changed = false;
  const fill = <T extends Pick<ProductTemplate, 'name' | 'nameI18n'>>(x: T, c: Pick<ProductTemplate, 'name' | 'nameI18n'>): T => {
    const have = storedNames(x), want = storedNames(c);
    const out: TextI18n = { ...x.nameI18n };
    let filled = false;
    for (const l of NAME_LANGS) {
      const v = have[l], w = want[l];
      if (w && (!v || (l !== 'en' && v === have.en && w !== have.en))) { out[l] = w; filled = true; }
    }
    if (!filled) return x;
    if (!out.en && have.en) out.en = have.en; // the English its `name` held, stated
    changed = true;
    return { ...x, nameI18n: out };
  };
  const product = fill(stored, code);
  const parts = stored.parts?.map((q) => {
    const twin = code.parts?.find((r) => r.id === q.id && storedNames(r).en === storedNames(q).en);
    return twin ? fill(q, twin) : q;
  });
  return changed ? { ...product, ...(parts ? { parts } : {}) } : null;
}

/** Names reviewed for a catalogue row: the product's and its parts' by id, each
 *  all three languages. */
export interface NamesProposal {
  name?: TextI18n;
  parts?: Record<string, TextI18n>;
}

/** A catalogue row with the reviewed names: each named object's `nameI18n`
 *  set to them and its `name` to their English, nothing else touched. Null
 *  when nothing changes (or the row lacks what is named). */
export function withNames(stored: ProductTemplate, names: NamesProposal): ProductTemplate | null {
  let changed = false;
  const set = <T extends Pick<ProductTemplate, 'name' | 'nameI18n'>>(x: T, n: TextI18n | undefined): T => {
    if (!n) return x;
    const next = { ...x, name: n.en ?? x.name, nameI18n: { ...n } };
    if (next.name === x.name && JSON.stringify(next.nameI18n) === JSON.stringify(x.nameI18n ?? {})) return x;
    changed = true;
    return next;
  };
  const product = set(stored, names.name);
  const parts = stored.parts?.map((q) => set(q, names.parts?.[q.id]));
  return changed ? { ...product, ...(parts ? { parts } : {}) } : null;
}
